import { bindMenuTabClose, swallowEscapeKeyup } from '@theme/coco-utilities';
const DROPDOWN_SELECTOR = '[data-localization-country-dropdown], [data-localization-language-dropdown]';

const OVERLAY_SELECTOR = '[data-localization-overlay], [data-announcement-localization-overlay], .localization-overlay';

const MENU_EASING = [0.16, 1, 0.3, 1];
const MENU_OPEN_DURATION = 0.25;
const MENU_CLOSE_DURATION = 0.2;
const MENU_OFFSET_Y = 12;
/** Bridges summary ↔ menu gap so hover leave does not close mid-travel. */
const HOVER_CLOSE_DELAY = 120;

let outsideClickBound = false;
let pointerDownInsideDropdown = false;
let bodyScrollLocked = false;
let bodyScrollTop = 0;

function getDropdowns() {
  return Array.from(document.querySelectorAll(DROPDOWN_SELECTOR));
}

function prefersReducedMotion() {
  return (
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ||
    !window.Motion?.animate
  );
}

function opensUpward(details) {
  return !!(
    details.classList.contains('localization-dropdown-up') ||
    details.closest('.localization-dropdown-up')
  );
}

function isCentered(details) {
  return !!(
    details.classList.contains('localization-dropdown-center') ||
    details.closest('.localization-dropdown-center')
  );
}

/**
 * Announcement (down): open top → bottom.
 * Footer / hamburger / mobile (up): open bottom → top.
 */
function getMenuTransforms(details) {
  const offsetY = opensUpward(details) ? MENU_OFFSET_Y : -MENU_OFFSET_Y;
  const x = isCentered(details) ? '-50%' : '0';

  return {
    hidden: `translate3d(${x}, ${offsetY}px, 0)`,
    visible: `translate3d(${x}, 0, 0)`,
  };
}

function stopMenuAnimation(menu) {
  if (!menu?._localizationAnim) return;
  try {
    menu._localizationAnim.stop?.();
  } catch (_) {
    /* ignore */
  }
  menu._localizationAnim = null;
}

async function animateMenu(menu, details, direction) {
  if (!menu) return;

  stopMenuAnimation(menu);

  const { hidden, visible } = getMenuTransforms(details);
  const isOpen = direction === 'open';

  if (prefersReducedMotion()) {
    menu.style.opacity = isOpen ? '1' : '0';
    menu.style.transform = isOpen ? visible : hidden;
    menu.style.visibility = isOpen ? 'visible' : 'hidden';
    return;
  }

  if (isOpen) {
    menu.style.visibility = 'visible';
    menu._localizationAnim = window.Motion.animate(
      menu,
      {
        opacity: [0, 1],
        transform: [hidden, visible],
      },
      {
        duration: MENU_OPEN_DURATION,
        easing: MENU_EASING,
      },
    );
  } else {
    menu._localizationAnim = window.Motion.animate(
      menu,
      {
        opacity: [1, 0],
        transform: [visible, hidden],
      },
      {
        duration: MENU_CLOSE_DURATION,
        easing: MENU_EASING,
      },
    );
  }

  await menu._localizationAnim.finished.catch(() => {});
  menu._localizationAnim = null;
}

function syncLocalizationOpenClass(details) {
  const form = details.closest('form');
  const parent = form?.parentElement;
  if (!parent) return;

  // [open] must apply to each selector — appending to a comma list only
  // targets the last one and leaves country dropdowns always "open".
  const anyOpen = Boolean(
    form.querySelector(
      '[data-localization-country-dropdown][open], [data-localization-language-dropdown][open]',
    ),
  );
  parent.classList.toggle('localization-open', anyOpen);
}

/**
 * Animated close — keeps <details open> until the exit motion finishes.
 * Uses a token so a cancelled hover-close cannot remove [open] later.
 */
async function closeDropdown(details) {
  if (!details?.open) return;
  if (details.dataset.closing === 'true') return;

  const menu = details.querySelector('.localization-menu');
  const token = {};
  details._closeToken = token;
  details.dataset.closing = 'true';

  try {
    await animateMenu(menu, details, 'close');
  } finally {
    if (details._closeToken !== token) return;
    details.removeAttribute('open');
    delete details.dataset.closing;
    details._closeToken = null;
    syncLocalizationOpenClass(details);
  }
}

/**
 * Hover re-entered while closing — cancel exit and restore open visuals.
 */
function cancelClosingDropdown(details) {
  if (details.dataset.closing !== 'true') return;

  details._closeToken = null;
  delete details.dataset.closing;

  const menu = details.querySelector('.localization-menu');
  if (!menu) return;

  stopMenuAnimation(menu);
  const { visible } = getMenuTransforms(details);
  menu.style.visibility = 'visible';
  menu.style.opacity = '1';
  menu.style.transform = visible;
}

function closeAllDropdowns(except = null) {
  getDropdowns().forEach((details) => {
    if (details === except) return;
    closeDropdown(details);
  });
}

function getOverlay(details) {
  const scope =
    details.closest('.announcement-bar-right') ||
    details.closest('form') ||
    document;

  return (
    scope.querySelector(OVERLAY_SELECTOR) ||
    document.querySelector(OVERLAY_SELECTOR)
  );
}

function setOverlayState(details, isOpen) {
  const overlay = getOverlay(details);

  if (!overlay) return;
  overlay.hidden = !isOpen;
  overlay.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

  if (overlay.dataset.localizationOverlayBound === 'true') return;
  overlay.dataset.localizationOverlayBound = 'true';
  overlay.addEventListener('click', (event) => {
    event.preventDefault();
    closeAllDropdowns();
  });
}

function isInsideDropdown(target) {
  return (
    target instanceof Element &&
    !!target.closest(DROPDOWN_SELECTOR)
  );
}

function isTouchDevice() {
  return (
    window.theme?.config?.isTouch ??
    ('ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0)
  );
}

/**
 * Desktop can use hover when configured; touch/mobile always uses tap/click.
 */
function getPreferredTrigger(details) {
  if (isTouchDevice()) return 'click';
  return details.dataset.trigger === 'hover' ? 'hover' : 'click';
}

function initLocalizationDropdowns() {
  const dropdowns = getDropdowns();
  dropdowns.forEach((details) => {
    if (details.dataset.bound === 'true') return;
    const form = details.closest('form');
    if (!form) return;
    details.dataset.bound = 'true';
    const summary = details.querySelector('summary');
    const menu = details.querySelector('.localization-menu');
    summary?.setAttribute('aria-expanded', 'false');
    let releaseMenuA11y = null;
    let menuPointerActive = false;

    /**
     * Hover open/close (desktop only).
     * Delay + cancel-on-reenter matches header dropdown and prevents
     * stuck-open when leave/re-enter races the close animation.
     */
    if (getPreferredTrigger(details) === 'hover') {
      let pointerInside = false;
      let hoverCloseTimer = null;

      details.addEventListener('mouseenter', () => {
        pointerInside = true;
        if (hoverCloseTimer) {
          clearTimeout(hoverCloseTimer);
          hoverCloseTimer = null;
        }
        cancelClosingDropdown(details);
        if (!details.open) {
          details.setAttribute('open', '');
        }
      });

      details.addEventListener('mouseleave', () => {
        pointerInside = false;
        if (hoverCloseTimer) clearTimeout(hoverCloseTimer);
        hoverCloseTimer = setTimeout(() => {
          hoverCloseTimer = null;
          if (pointerInside || !details.open) return;
          closeDropdown(details);
        }, HOVER_CLOSE_DELAY);
      });
    }

    /**
     * Intercept summary close so exit animation can run before [open] is removed.
     */
    summary?.addEventListener('click', (event) => {
      if (details.dataset.closing === 'true') {
        event.preventDefault();
        return;
      }
      if (!details.open) return;
      event.preventDefault();
      closeDropdown(details);
    });

    /**
     * Track interaction with the scrollable menu.
     *
     * This is especially useful for iPad/iOS where interacting
     * with the native scrollbar can cause <details> to close.
     */
    menu?.addEventListener('pointerdown', () => {
      menuPointerActive = true;
    });

    /**
     * Locale / Country selection.
     */
    details.addEventListener('click', (event) => {
      const localeButton = event.target.closest('[data-locale-code]');

      if (localeButton && details.contains(localeButton)) {
        const localeInput = form.querySelector(
          '[data-localization-locale-input]',
        );
        if (localeInput) {
          event.preventDefault();
          localeInput.value = localeButton.dataset.localeCode;
          form.submit();
        }
        return;
      }

      const countryButton = event.target.closest('[data-country-code]');

      if (countryButton && details.contains(countryButton)) {
        const countryInput = form.querySelector(
          '[data-localization-country-input]',
        );
        if (countryInput) {
          event.preventDefault();
          countryInput.value = countryButton.dataset.countryCode;
          form.submit();
        }
      }
    });

    /**
     * Handle dropdown open / close.
     */
    details.addEventListener('toggle', () => {
      if (details.open) {
        summary?.setAttribute('aria-expanded', 'true');
        releaseMenuA11y?.();
        releaseMenuA11y = bindMenuTabClose(
          details,
          () => {
            if (details.open) {
              closeDropdown(details);
            }
          },
          {
            // Keep false because scrollbar interaction can blur
            // focused elements on iPad/iOS.
            focusOut: false,
          },
        );
        // Close other localization dropdowns.
        closeAllDropdowns(details);
        // Show localization overlay.
        setOverlayState(details, true);
        syncLocalizationOpenClass(details);
        animateMenu(menu, details, 'open');
        return;
      }

      /**
       * Animated close just finished — run cleanup only.
       */
      if (details.dataset.closing === 'true') {
        summary?.setAttribute('aria-expanded', 'false');
        releaseMenuA11y?.();
        releaseMenuA11y = null;
        setOverlayState(details, false);
        syncLocalizationOpenClass(details);
        return;
      }

      /**
       * Re-open if browser/iOS closes <details> while
       * interacting with the native scrollbar.
       */
      if (menuPointerActive) {
        details.setAttribute('open', '');
        return;
      }

      summary?.setAttribute('aria-expanded', 'false');
      releaseMenuA11y?.();
      releaseMenuA11y = null;
      // Hide localization overlay.
      setOverlayState(details, false);
      syncLocalizationOpenClass(details);
    });

    /**
     * Close dropdown with Escape and return focus to the summary origin.
     * Swallow the matching keyup so a parent drawer (hamburger) stays open.
     */
    details.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !details.open) return;
      event.preventDefault();
      event.stopPropagation();
      swallowEscapeKeyup();
      closeDropdown(details);
      summary?.focus({ preventScroll: true });
    });

    /**
     * Clear the menu pointer flag after the gesture finishes.
     *
     * Small delay helps iPad/iOS complete scrollbar/touch gestures
     * before allowing the <details> state to change normally.
     */
    const clearMenuPointer = () => {
      setTimeout(() => {
        menuPointerActive = false;
      }, 50);
    };

    document.addEventListener('pointerup', clearMenuPointer);
    document.addEventListener('pointercancel', clearMenuPointer);
  });

  /**
   * Outside click handling.
   */
  if (!outsideClickBound) {
    outsideClickBound = true;

    document.addEventListener(
      'pointerdown',
      (event) => {
        pointerDownInsideDropdown = isInsideDropdown(event.target);
      },
      true,
    );

    document.addEventListener('click', (event) => {
      const startedInsideDropdown = pointerDownInsideDropdown;
      // Always reset the flag after handling the click.
      pointerDownInsideDropdown = false;
      /**
       * A scrollbar drag can start inside the dropdown
       * and finish outside it. Don't close the dropdown.
       */
      if (startedInsideDropdown) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      /**
       * Don't close when clicking inside the dropdown
       * or on its overlay.
       */
      if (
        target.closest(DROPDOWN_SELECTOR) ||
        target.closest(OVERLAY_SELECTOR)
      ) {
        return;
      }
      closeAllDropdowns();
    });
  }
}

/**
 * Initialize after DOM is ready.
 */
if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initLocalizationDropdowns,
  );
} else {
  initLocalizationDropdowns();
}

/**
 * Shopify Theme Editor support.
 */
document.addEventListener(
  'shopify:section:load',
  initLocalizationDropdowns,
);
